import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Search,
  Eye,
  Award,
  DollarSign,
  Users,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { PageLoader } from '@/components/ui/Spinner';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { useReferralUsers } from '@/hooks/useReferrals';
import { TIER_CONFIG, type ReferralUser, type ReferralUserFilters, type ReferralTier } from '@/types/referral.types';

const tierOptions = [
  { value: 'all', label: 'Todos os tiers' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'prata', label: 'Prata' },
  { value: 'ouro', label: 'Ouro' },
  { value: 'embaixador', label: 'Embaixador' },
];

function formatPhone(phone: string | undefined | null) {
  if (!phone) return '-';
  if (phone.startsWith('55') && phone.length >= 12) {
    const country = phone.slice(0, 2);
    const area = phone.slice(2, 4);
    const part1 = phone.slice(4, 9);
    const part2 = phone.slice(9);
    return `+${country} ${area} ${part1}-${part2}`;
  }
  return `+${phone}`;
}

export default function ReferralUsersListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ReferralUserFilters>({
    tier: 'all',
    page: 1,
    limit: 20,
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, isError } = useReferralUsers(filters);

  const handleSearch = () => {
    setFilters((prev) => ({
      ...prev,
      search: searchInput,
      page: 1,
    }));
  };

  const handleFilterChange = (key: keyof ReferralUserFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  // Helper to get tier - handles both currentTier and tier fields
  const getUserTier = (user: ReferralUser): ReferralTier => {
    return user.currentTier || user.tier || 'bronze';
  };

  // Helper to get credits - handles string and number formats
  const getUserCredits = (user: ReferralUser): number => {
    if (user.availableCredits) return parseFloat(user.availableCredits);
    if (user.credits) return user.credits;
    return 0;
  };

  const columns: Column<ReferralUser>[] = [
    {
      key: 'user',
      label: 'Usuario',
      render: (user) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {user.user?.name || user.name || 'Sem nome'}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {formatPhone(user.user?.phoneNumber || user.phoneNumber)}
          </p>
        </div>
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      render: (user) => {
        const tier = getUserTier(user);
        const config = TIER_CONFIG[tier];
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}
          >
            {config.label}
          </span>
        );
      },
    },
    {
      key: 'referrals',
      label: 'Indicacoes',
      render: (user) => (
        <div className="flex items-center gap-1">
          <Users className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            {user.validatedReferrals ?? 0}
          </span>
          {(user.pendingReferrals ?? 0) > 0 && (
            <span className="text-sm text-yellow-600 dark:text-yellow-400">
              (+{user.pendingReferrals})
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'credits',
      label: 'Creditos',
      render: (user) => (
        <div className="flex items-center gap-1">
          <DollarSign className="w-4 h-4 text-gray-400" />
          <span className="font-medium text-gray-900 dark:text-white">
            R$ {getUserCredits(user).toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      key: 'code',
      label: 'Codigo',
      render: (user) => (
        <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
          {user.referralCode || '-'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (user) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/referrals/users/${user.userId || user.id}`)}
        >
          <Eye className="w-4 h-4 mr-1" />
          Detalhes
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <PageLoader message="Carregando usuarios..." />;
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-red-500 dark:text-red-400">
          Erro ao carregar usuarios
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/admin/referrals">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Usuarios Referrers
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            Gerencie tiers e creditos dos usuarios
          </p>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {(['bronze', 'prata', 'ouro', 'embaixador'] as ReferralTier[]).map((tier) => {
          const config = TIER_CONFIG[tier];
          const count = data?.users.filter((u) => (u.currentTier || u.tier) === tier).length || 0;
          return (
            <Card key={tier} className={config.bgColor}>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Award className={`w-5 h-5 ${config.color}`} />
                  <div>
                    <p className={`text-sm font-medium ${config.color}`}>{config.label}</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome ou telefone..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                value={filters.tier || 'all'}
                onChange={(e) => handleFilterChange('tier', e.target.value as ReferralTier | 'all')}
                options={tierOptions}
              />
            </div>
            <Button onClick={handleSearch}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <DataTable
        data={data?.users || []}
        columns={columns}
        keyExtractor={(item) => item.id}
        emptyMessage="Nenhum usuario encontrado"
        pagination={{
          page: filters.page || 1,
          pageSize: filters.limit || 20,
          total: data?.total || 0,
          onPageChange: (page) => setFilters((prev) => ({ ...prev, page })),
        }}
      />
    </div>
  );
}
